import {
  Container,
  Graphics,
  Text,
  TextStyle,
} from "../../../vendor/pixi.min.mjs";
import { FROGGER_CONFIG } from "./froggerConfig.js";

const COIN_LABEL_STYLE = new TextStyle({
  fontFamily: "Arial",
  fontSize: 11,
  fontWeight: "bold",
  fill: "#78350f",
});

const STAR_LABEL_STYLE = new TextStyle({
  fontFamily: "Arial",
  fontSize: 11,
  fontWeight: "bold",
  fill: "#ffffff",
});

const {
  CELL_SIZE,
  COIN_COLOR,
  COIN_RADIUS,
  COIN_POINTS,
  STAR_COLOR,
  STAR_RADIUS,
  STAR_POINTS,
  STAR_WARN_ROWS,
  STAR_MISS_ROWS,
} = FROGGER_CONFIG;

/** Coin 3D-spin tuning — full rotation period in ms (slower = calmer). */
const COIN_SPIN_PERIOD_MS = 3000;

/**
 * Manages collectible items (coins + mandatory stars) on the Frogger board.
 * Items live in world-space and scroll with the camera.
 */
export class FroggerCollectibles {
  /** Add this to the world container */
  container = new Container();

  /** @type {Map<string, ItemData>} "col,row" → item data */
  _items = new Map();

  _originX = 0;
  _themeManager = null;

  constructor(originX, themeManager = null) {
    this._originX = originX;
    this._themeManager = themeManager;
  }

  // ── adding items ───────────────────────────────────

  addCoin(worldRow, gridCol) {
    const key = `${gridCol},${worldRow}`;
    if (this._items.has(key)) return;

    const gfx = this._buildCoinDisplay();

    this._positionGfx(gfx, worldRow, gridCol);
    this.container.addChild(gfx);

    this._items.set(key, {
      type: "coin",
      worldRow,
      gridCol,
      gfx,
      points: COIN_POINTS,
      collected: false,
      spinPhase: Math.random() * Math.PI * 2,
      spinSpeed: 0.85 + Math.random() * 0.3,
      spinDir: Math.random() < 0.5 ? -1 : 1,
    });
  }

  addStar(worldRow, gridCol) {
    const key = `${gridCol},${worldRow}`;
    if (this._items.has(key)) return;

    const gfx = this._buildStarDisplay();

    this._positionGfx(gfx, worldRow, gridCol);
    this.container.addChild(gfx);

    this._items.set(key, {
      type: "star",
      worldRow,
      gridCol,
      gfx,
      points: STAR_POINTS,
      collected: false,
    });
  }

  // ── collection ─────────────────────────────────────

  /**
   * Check if there's a collectible at the player's position.
   * @returns {{ type: string, points: number } | null}
   */
  checkCollection(gridCol, worldRow) {
    const key = `${gridCol},${worldRow}`;
    const item = this._items.get(key);
    if (!item || item.collected) return null;

    item.collected = true;
    this.container.removeChild(item.gfx);
    item.gfx.destroy();
    this._items.delete(key);

    return { type: item.type, points: item.points };
  }

  // ── missed-star detection ──────────────────────────

  /**
   * Find stars the player has passed without collecting.
   * @param {number} playerRow
   * @returns {{ worldRow: number, gridCol: number }[]}
   */
  getMissedStars(playerRow) {
    const missed = [];
    for (const [key, item] of this._items) {
      if (item.type !== "star" || item.collected) continue;
      if (playerRow - item.worldRow >= STAR_MISS_ROWS) {
        missed.push({ worldRow: item.worldRow, gridCol: item.gridCol });
      }
    }
    return missed;
  }

  /** Remove missed stars from the board after penalty is applied. */
  removeMissedStars(playerRow) {
    for (const [key, item] of this._items) {
      if (item.type !== "star" || item.collected) continue;
      if (playerRow - item.worldRow >= STAR_MISS_ROWS) {
        this.container.removeChild(item.gfx);
        item.gfx.destroy();
        this._items.delete(key);
      }
    }
  }

  // ── animation ──────────────────────────────────────

  /**
   * Per-frame animation:
   *   - Coins spin around their vertical axis (scaleX = cos(t)) to read as
   *     a 3-D disc with thickness — the sprite squashes to a thin sliver
   *     each half-cycle, mimicking edge-on view. ScaleY breathes subtly.
   *   - Stars stay steady; only blink when about to be missed.
   */
  pulse(playerRow) {
    const now = Date.now();

    for (const [, item] of this._items) {
      if (item.collected) continue;

      if (item.type === "star") {
        const rowsAhead = playerRow - item.worldRow;
        if (rowsAhead >= STAR_WARN_ROWS) {
          item.gfx.alpha = Math.sin(now * 0.02) > 0 ? 1 : 0.25;
          item.gfx.scale.set(0.95 + 0.15 * Math.abs(Math.sin(now * 0.008)));
        } else {
          item.gfx.alpha = 1;
          item.gfx.scale.set(0.96 + 0.06 * Math.sin(now * 0.004));
        }
      } else {
        item.gfx.alpha = 1;
        const phase =
          item.spinPhase +
          item.spinDir *
            (now / COIN_SPIN_PERIOD_MS) *
            Math.PI *
            2 *
            item.spinSpeed;
        const sx = Math.cos(phase);
        const absSx = Math.abs(sx);
        const face = item.gfx.coinFace;
        if (face) {
          face.scale.x = sx;
          face.scale.y = 0.93 + 0.07 * absSx;
        } else {
          item.gfx.scale.x = sx;
        }
        const edge = item.gfx.edgeGfx;
        if (edge) {
          edge.alpha = 1 - absSx * 0.35;
        }
      }
    }
  }

  // ── cleanup ────────────────────────────────────────

  /** Remove items on rows that have been cleaned up by RowManager. */
  cleanupOutside(lowRow, highRow) {
    for (const [key, item] of this._items) {
      if (item.worldRow < lowRow || item.worldRow > highRow) {
        this.container.removeChild(item.gfx);
        item.gfx.destroy();
        this._items.delete(key);
      }
    }
  }

  destroy() {
    for (const [, item] of this._items) {
      item.gfx.destroy();
    }
    this._items.clear();
    this.container.removeChildren();
  }

  // ── internals ──────────────────────────────────────

  _positionGfx(gfx, worldRow, gridCol) {
    gfx.x = this._originX + gridCol * CELL_SIZE + CELL_SIZE / 2;
    gfx.y = -worldRow * CELL_SIZE + CELL_SIZE / 2;
  }

  _buildCoinDisplay() {
    const size = CELL_SIZE * 0.9;
    const wrap = new Container();

    // Thickness band: a wide-ish "rim" rectangle behind the face.
    // It's progressively revealed as the face squashes to scaleX → 0 (edge-on
    // view), selling the illusion that the coin is a real 3-D disc.
    // Layered: dark shadow base + brighter highlight stripe + dark rim line.
    const edge = new Graphics();
    const edgeW = size * 0.14;
    const edgeH = size * 0.96 + 2;
    const r = edgeW / 2;

    edge.roundRect(-edgeW / 2, -edgeH / 2, edgeW, edgeH, r);
    edge.fill({ color: 0xf2c14e, alpha: 1 });

    wrap.addChild(edge);
    wrap.edgeGfx = edge;

    const faceSize = size * 0.88;
    const sprite = this._themeManager?.createSprite(
      "frogger.collectible.coin",
      faceSize,
      faceSize,
      { anchorX: 0.5, anchorY: 0.5, fitMode: "contain", scaleMultiplier: 1 },
    );
    if (sprite) {
      wrap.addChild(sprite);
      wrap.coinFace = sprite;
      return wrap;
    }

    const gfx = new Graphics();
    gfx.circle(0, 0, (size * 0.88) / 2);
    gfx.fill(COIN_COLOR);
    const label = new Text({ text: "$", style: COIN_LABEL_STYLE });
    label.anchor.set(0.5);
    gfx.addChild(label);
    wrap.addChild(gfx);
    wrap.coinFace = gfx;
    return wrap;
  }

  _buildStarDisplay() {
    const size = CELL_SIZE * 0.92;
    const sprite = this._themeManager?.createSprite(
      "frogger.collectible.star",
      size,
      size,
      { fitMode: "contain", scaleMultiplier: 1 },
    );
    if (sprite) {
      const wrap = new Container();
      wrap.addChild(sprite);
      return wrap;
    }

    const gfx = new Graphics();
    this._drawStar(gfx, 0, 0, size / 2, STAR_COLOR);
    const label = new Text({ text: "!", style: STAR_LABEL_STYLE });
    label.anchor.set(0.5);
    gfx.addChild(label);
    return gfx;
  }

  _drawStar(g, cx, cy, r, color) {
    const points = 5;
    const innerR = r * 0.4;
    const step = Math.PI / points;

    g.moveTo(cx, cy - r);
    for (let i = 0; i < 2 * points; i++) {
      const angle = -Math.PI / 2 + step * (i + 1);
      const rad = i % 2 === 0 ? innerR : r;
      g.lineTo(cx + Math.cos(angle) * rad, cy + Math.sin(angle) * rad);
    }
    g.closePath();
    g.fill(color);
  }
}
